/**
 * Messages that have been sent and have no row yet.
 *
 * Module state rather than component state, and that is the whole point. The
 * turn is debounced server-side, so nothing is written until it starts, and the
 * panel unmounts when it closes -- so a reader who sent something and closed
 * the widget inside that window came back to a thread with their own message
 * missing from it.
 *
 * Lives for the tab, deliberately. A reload is a new session and the row has
 * either landed by then or never will, so persisting past it would restore
 * bubbles the server has no memory of.
 */

import type { ThreadMessage } from "./message-thread";

interface PendingInput {
  /**
   * The id the client gave this message, which the server stores as the row's
   * `externalMessageId` and the read hands back.
   *
   * Matching on it is the whole mechanism, and it replaced four attempts to
   * infer the same thing. Body text cannot tell two identical messages apart,
   * nor two captionless images. A count over the visible page cannot survive
   * the page. Per-message timestamps cannot survive a turn already in flight,
   * whose rows are newer than a send that came after it. And a count against
   * the oldest bubble re-credits rows it has already spent, taking an unlanded
   * message off the screen on the next poll.
   *
   * All four were guessing at something the client can simply say.
   */
  id: string;
  body: string;
  images: { url: string; mimeType: string }[];
  at: number;
}

/**
 * How long a drawn message may outlive its row.
 *
 * The webhook is acked before the agent runs, so a turn can be accepted and
 * then die writing nothing. Past this, a bubble is claiming a message went out
 * that never did, which is worse than losing the draft.
 */
const PENDING_CEILING_MS = 90_000;

let pending: PendingInput[] = [];

export function addPending(send: PendingInput): void {
  pending = [...pending, send];
}

/**
 * The pending messages still worth drawing, oldest first.
 *
 * `storedIds` are the `externalMessageId`s the read returned. A pending message
 * whose id is among them has its row and stops being ours to draw; order does
 * not matter, and neither does how many times the same row is seen.
 */
export function settlePending(
  storedIds: readonly string[],
  now: number,
): ThreadMessage[] {
  const landed = new Set(storedIds);
  pending = pending.filter(
    (send) => !landed.has(send.id) && now - send.at < PENDING_CEILING_MS,
  );
  return pending.map((send) => ({
    id: `pending-${send.id}`,
    // The identity that survives the swap. The row the server returns carries
    // the same value, so keying on it keeps one element across the moment the
    // bubble stops being ours to draw -- see `message-thread`.
    clientId: send.id,
    role: "EXPERT",
    body: send.body,
    channel: "WEB",
    createdAt: new Date(send.at),
    pending: true,
    images: send.images.length > 0 ? send.images : undefined,
  }));
}

/**
 * Take a message back, returning what it was carrying.
 *
 * For a send the transport refused: it will never become a row, so nothing else
 * would take its bubble down, and one that stays is an undelivered message
 * drawn as a delivered one. Handing the body back lets the caller put it where
 * the reader can try again rather than losing what they typed.
 */
export function dropPending(
  id: string,
): { body: string; images: { url: string; mimeType: string }[] } | null {
  const going = pending.find((send) => send.id === id);
  if (!going) {
    return null;
  }
  pending = pending.filter((send) => send.id !== id);
  return { body: going.body, images: going.images };
}

/**
 * When the oldest drawn message stops being worth drawing, or null if there is
 * none.
 *
 * The ceiling is only applied when someone calls `settlePending`, and the only
 * thing that called it was a stored row arriving. On a turn that dies writing
 * nothing, no row ever arrives -- so the check never ran and the bubble stayed
 * for the life of the tab. The caller uses this to come back on its own.
 */
export function nextExpiryAt(): number | null {
  const oldest = pending[0];
  return oldest ? oldest.at + PENDING_CEILING_MS : null;
}

/** Test seam: module state outlives a test file otherwise. */
export function clearPending(): void {
  pending = [];
}
